/**
 * portalTheme.js — Spacemancraft Portal Shell Design Tokens
 *
 * Flat dark mission-control aesthetic.
 * Import this in any portal shell component:
 *
 *   import { PT, ptStyle, ptCSS } from '../design/portalTheme';
 *
 * Usage:
 *   <div style={ptStyle.rail}>...</div>
 *   <style>{ptCSS}</style>
 */

/* ──────────────────────────────────────────────────────────
   Color tokens — mirror the --smc-* CSS vars but as JS values
   so portals can use them in inline style objects
   ────────────────────────────────────────────────────────── */
export const PT = {
  bg:        '#0a0f1e',
  bgDeep:    '#060b18',
  panel:     '#0d1526',
  panel2:    '#111b30',
  panel3:    '#0b1325',
  hairline:  'rgba(255,255,255,0.06)',
  hairline2: 'rgba(255,255,255,0.10)',

  fg:        '#e6ecf7',
  fgMute:    '#9aa8c2',
  fgDim:     '#6b7894',

  cyan:      '#0e9cc0',
  cyanInk:   '#22d3eb',
  cyanSoft:  'rgba(14,156,192,0.10)',
  cyanLine:  'rgba(14,156,192,0.30)',

  green:     '#22c55e',
  greenInk:  '#4ade80',
  greenSoft: 'rgba(34,197,94,0.10)',
  greenLine: 'rgba(34,197,94,0.30)',

  amber:     '#f59e0b',
  amberInk:  '#fbbf24',
  amberSoft: 'rgba(245,158,11,0.10)',
  amberLine: 'rgba(245,158,11,0.30)',

  purple:    '#a78bfa',
  purpleSoft:'rgba(167,139,250,0.10)',
  purpleLine:'rgba(167,139,250,0.30)',

  red:       '#f43f5e',
  redSoft:   'rgba(244,63,94,0.10)',
  redLine:   'rgba(244,63,94,0.30)',

  sans: "'Inter', -apple-system, system-ui, sans-serif",
  mono: "'Share Tech Mono', ui-monospace, Menlo, monospace",
  hl:   '0.5px',
};

/* ──────────────────────────────────────────────────────────
   Accent helper — returns per-portal toned tokens
   call: const A = ptAccent('#0e9cc0')
   ────────────────────────────────────────────────────────── */
export function ptAccent(hex) {
  return {
    ink:    hex,
    soft:   hex + '1a',
    line:   hex + '4d',
    bg:     `rgba(${hexToRgb(hex)},0.10)`,
    border: `rgba(${hexToRgb(hex)},0.30)`,
    btn:    { background: `rgba(${hexToRgb(hex)},0.10)`, border: `0.5px solid rgba(${hexToRgb(hex)},0.30)`, color: hex },
  };
}

function hexToRgb(hex) {
  const h = hex.replace('#','');
  const n = parseInt(h.length === 3
    ? h.split('').map(c=>c+c).join('')
    : h, 16);
  return `${(n>>16)&255},${(n>>8)&255},${n&255}`;
}

/* ──────────────────────────────────────────────────────────
   Pre-built style objects for shell primitives
   ────────────────────────────────────────────────────────── */
export const ptStyle = {

  /* Shell wrapper — two-column: sidebar + main */
  shell: {
    display: 'flex',
    minHeight: '100vh',
    background: PT.bg,
    color: PT.fg,
    fontFamily: PT.sans,
    fontSize: 13,
    lineHeight: 1.5,
  },

  /* Main column — flex column filling remaining width */
  main: {
    flex: '1 1 auto',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },

  /* Top bar */
  topbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '0 22px',
    height: 52,
    borderBottom: `0.5px solid ${PT.hairline}`,
    background: PT.bgDeep,
    flexShrink: 0,
  },

  /* Portal identity block in topbar */
  portalId: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
    minWidth: 180,
  },

  /* Portal icon box */
  portalIconBox: (accentHex = PT.cyan) => ({
    width: 28, height: 28, borderRadius: 6,
    background: `rgba(${hexToRgb(accentHex)},0.10)`,
    border: `0.5px solid rgba(${hexToRgb(accentHex)},0.30)`,
    color: accentHex,
    display: 'grid', placeItems: 'center',
    flexShrink: 0,
  }),

  /* Search bar */
  search: {
    flex: '1 1 auto',
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    background: PT.panel,
    border: `0.5px solid ${PT.hairline}`,
    borderRadius: 5,
    padding: '7px 11px',
    maxWidth: 520,
  },

  searchInput: {
    flex: '1 1 auto',
    minWidth: 0,
    background: 'transparent',
    border: 0,
    outline: 0,
    color: PT.fg,
    fontSize: 12.5,
    fontFamily: PT.sans,
  },

  /* Segment button group */
  segGroup: {
    display: 'inline-flex',
    gap: 1,
  },

  segBtn: (active = false, accentHex = PT.cyan) => ({
    fontFamily: PT.mono,
    fontSize: 10,
    letterSpacing: '0.14em',
    padding: '6px 10px',
    border: `0.5px solid ${active ? `rgba(${hexToRgb(accentHex)},0.30)` : PT.hairline}`,
    background: active ? `rgba(${hexToRgb(accentHex)},0.10)` : PT.panel,
    color: active ? accentHex : PT.fgDim,
    cursor: 'pointer',
    transition: 'all 0.12s',
  }),

  /* New report / action button */
  ctaBtn: (accentHex = PT.cyan) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    padding: '7px 13px',
    borderRadius: 5,
    border: `0.5px solid rgba(${hexToRgb(accentHex)},0.30)`,
    background: `rgba(${hexToRgb(accentHex)},0.10)`,
    color: accentHex,
    fontFamily: PT.sans,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  }),

  dividerV: {
    width: '0.5px',
    height: 22,
    background: PT.hairline,
    flexShrink: 0,
  },

  /* ── Left rail ── */
  rail: {
    width: 240,
    flexShrink: 0,
    borderRight: `0.5px solid ${PT.hairline}`,
    background: PT.bg,
    overflowY: 'auto',
    padding: '18px 14px 28px',
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },

  /* Portal info card inside rail */
  infoCard: {
    background: PT.panel,
    border: `0.5px solid ${PT.hairline}`,
    borderRadius: 6,
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 9,
  },

  infoHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },

  /* 2×2 stats grid */
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 1,
    background: PT.hairline,
    border: `0.5px solid ${PT.hairline}`,
    borderRadius: 5,
    overflow: 'hidden',
  },

  statCell: {
    background: PT.panel,
    padding: '10px 12px',
  },

  /* Section label */
  sectionLabel: {
    fontFamily: PT.mono,
    fontSize: 9,
    letterSpacing: '0.22em',
    color: PT.fgDim,
    marginBottom: 8,
    marginTop: 4,
  },

  /* Category row */
  catRow: (active = false, accentHex = PT.cyan) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 10px',
    borderRadius: 4,
    cursor: 'pointer',
    border: `0.5px solid ${active ? `rgba(${hexToRgb(accentHex)},0.28)` : 'transparent'}`,
    background: active ? `rgba(${hexToRgb(accentHex)},0.08)` : 'transparent',
    color: active ? accentHex : PT.fgMute,
    fontSize: 12,
    fontWeight: 500,
    transition: 'all 0.12s',
    userSelect: 'none',
  }),

  /* Feed area */
  feed: {
    flex: '1 1 auto',
    minWidth: 0,
    overflowY: 'auto',
    padding: '20px 20px 60px',
  },

  /* Feed header */
  feedHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: `0.5px solid ${PT.hairline}`,
  },

  /* Report card */
  reportCard: (pinned = false) => ({
    background: PT.panel,
    border: `0.5px solid ${pinned ? 'rgba(245,158,11,0.25)' : PT.hairline}`,
    borderRadius: 6,
    padding: '14px 16px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  }),

  /* Thread panel */
  threadPanel: {
    width: 260,
    flexShrink: 0,
    borderLeft: `0.5px solid ${PT.hairline}`,
    background: PT.panel3,
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 52px)',
    position: 'sticky',
    top: 52,
    overflow: 'hidden',
  },

  /* Thread toggle strip */
  threadToggle: {
    writingMode: 'vertical-rl',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    padding: '14px 6px',
    cursor: 'pointer',
    background: PT.bgDeep,
    borderLeft: `0.5px solid ${PT.hairline}`,
    userSelect: 'none',
    position: 'sticky',
    top: 52,
    height: 'calc(100vh - 52px)',
    flexShrink: 0,
  },

  /* Avatar */
  avatar: (tone = 'cyan', size = 28) => ({
    width: size, height: size, borderRadius: '50%',
    background: PT.panel2,
    border: `0.5px solid ${PT.hairline2}`,
    display: 'grid', placeItems: 'center',
    fontFamily: PT.mono,
    fontSize: size * 0.38,
    color: ({
      cyan: PT.cyanInk, green: PT.greenInk, amber: PT.amberInk,
      purple: PT.purple, red: '#f87171',
    })[tone] || PT.fgMute,
    flexShrink: 0,
    overflow: 'hidden',
  }),

  /* Status pill */
  pill: (tone = 'cyan') => {
    const tones = {
      cyan:   { color: PT.cyanInk,  bg: PT.cyanSoft,  border: PT.cyanLine },
      green:  { color: PT.greenInk, bg: PT.greenSoft,  border: PT.greenLine },
      amber:  { color: PT.amberInk, bg: PT.amberSoft,  border: PT.amberLine },
      purple: { color: PT.purple,   bg: PT.purpleSoft, border: PT.purpleLine },
    };
    const t = tones[tone] || tones.cyan;
    return {
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 8px', borderRadius: 3,
      background: t.bg, border: `0.5px solid ${t.border}`, color: t.color,
      fontFamily: PT.mono, fontSize: 9, letterSpacing: '0.14em',
    };
  },
};

/* ──────────────────────────────────────────────────────────
   CSS string — inject once per portal via <style>{ptCSS}</style>
   Covers scrollbar, no-focus ring, base resets.
   ────────────────────────────────────────────────────────── */
export const ptCSS = `
  .pt-rail::-webkit-scrollbar { width: 3px; }
  .pt-rail::-webkit-scrollbar-track { background: transparent; }
  .pt-rail::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }

  .pt-feed::-webkit-scrollbar { width: 4px; }
  .pt-feed::-webkit-scrollbar-track { background: transparent; }
  .pt-feed::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.10); border-radius: 2px; }

  .pt-thread-panel::-webkit-scrollbar { width: 3px; }
  .pt-thread-panel::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }

  .pt-seg-btn:first-child { border-radius: 4px 0 0 4px !important; }
  .pt-seg-btn:last-child  { border-radius: 0 4px 4px 0 !important; }

  .pt-search-input:focus { outline: none; }

  .pt-cat-row:hover { background: rgba(255,255,255,0.02) !important; color: #c8d4e8 !important; }

  .pt-thread-toggle:hover { background: rgba(14,156,192,0.05) !important; }

  .pt-report-card:hover { border-color: rgba(255,255,255,0.12) !important; }

  @keyframes pt-shimmer {
    0%   { opacity: 0.45; }
    50%  { opacity: 0.75; }
    100% { opacity: 0.45; }
  }
`;

/* ──────────────────────────────────────────────────────────
   Theme-adaptive token builder
   Maps a global app theme (from appTheme.js / useTheme()) to
   portal-compatible PT tokens so portals respond to Dark/Light/Space.
   ────────────────────────────────────────────────────────── */
export function buildPT(theme) {
  if (!theme) return PT;
  const isDark = theme.dark !== false;
  return {
    ...PT,
    bg:        theme.bg,
    bgDeep:    theme.eqBg    || theme.bg,
    panel:     theme.panel,
    panel2:    theme.cardBg  || theme.panel,
    panel3:    theme.eqBg    || theme.bg,
    hairline:  isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)',
    hairline2: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.13)',
    fg:        theme.bright,
    fgMute:    theme.text,
    fgDim:     theme.muted,
    greenInk:  isDark ? '#4ade80' : '#16a34a',
    amberInk:  isDark ? '#fbbf24' : '#d97706',
    cyanInk:   isDark ? '#22d3eb' : '#0284c7',
  };
}

/* ──────────────────────────────────────────────────────────
   Mono label helper
   ────────────────────────────────────────────────────────── */
export function ptLabel(text, opts = {}) {
  return {
    fontFamily: PT.mono,
    fontSize: opts.size || 9,
    letterSpacing: opts.spacing || '0.18em',
    color: opts.color || PT.fgDim,
    textTransform: 'uppercase',
    ...opts.style,
  };
}
